import { TIconNames } from 'components/Icon';
import { Colors } from 'core/CssVariables';
import { TFetchConverter } from 'data/types/converter.types';
import {
  TGaugeContentMapper,
  TGaugeMetaData,
  TGaugeMetaDataUi,
  TGauges,
  GaugesColorEnum,
  TGaugesMapperColor,
  GaugesEnum,
  TGaugesUi,
  TTooltipTitleMapper,
  TooltipTitlesEnum,
} from 'data/types/gauges.types';

export const gaugesMapperColor: TGaugesMapperColor = {
  [GaugesColorEnum.INFO]: Colors.SuccessColor,
  [GaugesColorEnum.WARNING]: Colors.WarningColor,
  [GaugesColorEnum.ALERT]: Colors.ErrorColor,
  [GaugesColorEnum.TEXT]: Colors.SecondaryColor,
};

export const tooltipTitleMapper: TTooltipTitleMapper = {
  [TooltipTitlesEnum.openTickets]: {
    collapsed: 'Open tickets',
    expanded: 'Total',
  },
  [TooltipTitlesEnum.totalNewTicket]: {
    collapsed: 'New tickets',
    expanded: 'Total',
  },
  [TooltipTitlesEnum.frSlaFailedTickets]: {
    collapsed: 'FR SLA Failed',
    expanded: 'FR SLA Failed',
  },
  [TooltipTitlesEnum.overdueTickets]: {
    collapsed: 'Overdue tickets',
    expanded: 'Total',
  },
  [TooltipTitlesEnum.overdueTicketsMoreThanHour]: {
    collapsed: 'More than an hour',
    expanded: 'More than an hr',
  },
  [TooltipTitlesEnum.overdueTasks]: {
    collapsed: 'Overdue tasks',
    expanded: 'Total',
  },
  [TooltipTitlesEnum.overdueTasksMoreThanHour]: {
    collapsed: 'More than an hour',
    expanded: 'More than an hr',
  },
  [TooltipTitlesEnum.closeToFailSlas]: {
    collapsed: 'Tickets near SLA failure',
    expanded: 'Close to fail SLA',
  },
  [TooltipTitlesEnum.avgScore]: {
    collapsed: 'Average urgency',
    expanded: 'Avg score',
  },
  [TooltipTitlesEnum.customersNeedingAttention]: {
    // ZEST-735: keep the "expanded" label concise so it fits the one-row
    // dashbar. This value is also shown in the mobile gauge dropdown (where
    // there is more room), but a consistently short label reads fine there too;
    // the full wording remains on the collapsed/tooltip hover.
    collapsed: 'Customers needing attention',
    expanded: 'Needs attn',
  },
  [TooltipTitlesEnum.failedSlas]: {
    collapsed: 'Tickets which failed SLA',
    expanded: 'SLA failed tickets',
  },
};

export const gaugeContentMapper: TGaugeContentMapper = {
  [GaugesEnum.OPEN_TICKETS]: {
    name: 'Open Tickets',
    description: 'Total number of open and new tickets currently.',
    icon: null,
  },
  [GaugesEnum.NEW_TICKETS]: {
    name: 'New Tickets',
    description:
      'Current new tickets: green = none, yellow = 1+, red = 1+ SLA failed.',
    icon: TIconNames.TICKET,
  },
  [GaugesEnum.OVERDUE_TICKETS]: {
    name: 'Overdue Tickets',
    description:
      'Overdue tickets: green = none, yellow = overdue < 1hr, red = overdue > 1hr.',
    icon: TIconNames.OVERDUE_TICKET,
    subLabel: { forKey: TooltipTitlesEnum.overdueTickets, text: 'Total' },
  },
  [GaugesEnum.OVERDUE_TASKS]: {
    name: 'Overdue Tasks',
    description:
      'Overdue tasks: green = none, yellow = overdue < 1hr, red = overdue > 1hr.',
    icon: TIconNames.OVERDUE_TASK,
  },
  [GaugesEnum.SLA_CLOSE_TO_FAIL]: {
    name: 'SLA Health',
    description:
      'SLA health: green = none at risk, yellow = ticket < 8hrs to SLA, red = SLA failed.',
    icon: TIconNames.SLA,
    subLabel: {
      forKey: TooltipTitlesEnum.closeToFailSlas,
      text: 'Close to fail',
    },
  },
  [GaugesEnum.AVG_URGENCY]: {
    name: 'Average Urgency',
    description:
      'Average urgency of open/new tickets: green = below 5, yellow = 5-8, red = above 8.',
    icon: TIconNames.URGENCY,
    /* The design prints "7.4 / 10", but urgency is a 0-N severity score with no
       ceiling — live values already exceed 10 — so a denominator would read as
       a bug. Name the metric instead. */
    subLabel: { forKey: TooltipTitlesEnum.avgScore, text: 'Avg score' },
  },
  [GaugesEnum.CUSTOMERS_NEEDING_INTERACTION]: {
    name: 'Customer Health',
    description:
      "Customer health gauge helps to monitor relationships with customer and track block hours. Customer is considered healthy if it has non overdue scheduled interaction. Also, for the block hours enabled customers the remaining hours should be at least 10% and hours shouldn't be expired",
    icon: TIconNames.CUSTOMER_HEALTH,
    subLabel: {
      forKey: TooltipTitlesEnum.customersNeedingAttention,
      text: 'Need attention',
    },
  },
};

export const gaugeMetaDataConverter: TFetchConverter<
  TGaugeMetaDataUi,
  TGaugeMetaData
> = {
  fromDb: data => {
    return {
      title: tooltipTitleMapper[data.key].collapsed,
      color: gaugesMapperColor[data.severity],
      value: data.value,
      expanedTitle: tooltipTitleMapper[data.key].expanded,
    };
  },
};

export const gaugesConverter: TFetchConverter<TGaugesUi, TGauges> = {
  fromDb: data => {
    return {
      type: data.type,
      color: gaugesMapperColor[data.severity],
      gaugeContent: gaugeContentMapper[data.type],
      metaData: data.metaData
        ? data.metaData.map(item => gaugeMetaDataConverter.fromDb(item))
        : [],
    };
  },
};

export const allGaugesConverter: TFetchConverter<
  TGaugesUi[],
  { [key in GaugesEnum]: TGauges }
> = {
  fromDb: data => {
    const gaugeOrder = Object.values(GaugesEnum);
    return Object.keys(data).length
      ? gaugeOrder.map((item: GaugesEnum) => gaugesConverter.fromDb(data[item]))
      : [];
  },
};
